import { useState } from 'react';
import Hero from '../../components/Hero';
import Stats from '../../components/Stats';
import ModuleNav from '../../components/Modules/ModuleNav';
import ModuleBlock from '../../components/Modules/ModuleBlock';
import Features from '../../components/Features';
import Contact from '../../components/Contact';
import ScrollToTop from '../../common/ScrollToTop';
import { usePageMetadata, ROUTE_METADATA } from '../../hooks/usePageMetadata';

import DashboardContent from '../../content/DashboardContent.json';
import PatientRegistrationContent from '../../content/PatientRegistrationContent.json';
import OPDContent from '../../content/OPDContent.json';
import IPDContent from '../../content/IPDContent.json';
import AppointmentContent from '../../content/AppointmentContent.json';
import ATDContent from '../../content/ATDContent.json';
import DischargeSummaryContent from '../../content/DischargeSummaryContent.json';
import ReportsContent from '../../content/ReportsContent.json';
import AdminContent from '../../content/AdminContent.json';

const Home = () => {
  usePageMetadata(ROUTE_METADATA['/']);
  const [activeModule, setActiveModule] = useState('dashboard');

  const scrollTo = (id: string) => {
    setActiveModule(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleRequestDemo = () => {
    scrollTo('contact');
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Floating Scroll to Top button */}
      <ScrollToTop />

      {/* Hero Section */}
      <Hero />

      {/* Key Numbers / Stats */}
      <Stats />

      {/* Module Navigation anchor */}
      <div id="modules">
        <ModuleNav activeModule={activeModule} onSelectModule={scrollTo} />
      </div>

      {/* Core Hospital Modules */}
      <div className="space-y-4">
        {/* 1. Dashboard */}
        <ModuleBlock
          id="dashboard"
          category="Executive & Medical Overview"
          title={DashboardContent.title}
          content={DashboardContent.text}
          contentList={DashboardContent.contentList}
          icon="dashboard_ss.png"
          direction="left"
          badge="Live Hospital Census"
          onRequestDemo={handleRequestDemo}
        />

        {/* 2. Patient Registration */}
        <ModuleBlock
          id="patient-registration"
          category="Front Desk & Patient Intake"
          title={PatientRegistrationContent.title}
          content={PatientRegistrationContent.text}
          contentList={PatientRegistrationContent.contentList}
          icon="patient_register_ss.png"
          direction="right"
          badge="Quick UHID Creation"
          onRequestDemo={handleRequestDemo}
        />

        {/* 3. OPD Services */}
        <ModuleBlock
          id="opd"
          category="Outpatient Department & Billing"
          title={OPDContent.title}
          content={OPDContent.text}
          contentList={OPDContent.contentList}
          icon="opd_ss.png"
          direction="left"
          badge="Instant PDF Billing"
          onRequestDemo={handleRequestDemo}
        />

        {/* 4. IPD Care */}
        <ModuleBlock
          id="ipd"
          category="Inpatient Management"
          title={IPDContent.title}
          content={IPDContent.text}
          contentList={IPDContent.contentList}
          icon="ipd_ss.png"
          direction="right"
          badge="Complete Clinical History"
          onRequestDemo={handleRequestDemo}
        />

        {/* 5. Appointment Management */}
        <ModuleBlock
          id="appointment"
          category="Doctor Rosters & Scheduling"
          title={AppointmentContent.title}
          content={AppointmentContent.text}
          contentList={AppointmentContent.contentList}
          icon="appointment_ss.png"
          direction="left"
          badge="Zero Slot Conflicts"
          onRequestDemo={handleRequestDemo}
        />

        {/* 6. ATD (Bed Management) */}
        <ModuleBlock
          id="atd"
          category="Smart Bed Allocation & Transfers"
          title={ATDContent.title}
          content={ATDContent.text}
          contentList={ATDContent.contentList}
          icon="atd_ss.png"
          direction="right"
          badge="Ward & ICU Availability"
          onRequestDemo={handleRequestDemo}
        />

        {/* 7. Discharge Summary */}
        <ModuleBlock
          id="discharge"
          category="Standardized Medical Discharge"
          title={DischargeSummaryContent.title}
          content={DischargeSummaryContent.text}
          contentList={DischargeSummaryContent.contentList}
          icon="discharge_summary_ss.png"
          direction="left"
          badge="Print-Ready Reports"
          onRequestDemo={handleRequestDemo}
        />

        {/* 8. Reports & Analytics */}
        <ModuleBlock
          id="reports"
          category="Hospital Intelligence & Auditing"
          title={ReportsContent.title}
          content={ReportsContent.text}
          contentList={ReportsContent.contentList}
          icon="report_ss.png"
          direction="right"
          badge="Excel & CSV Exports"
          onRequestDemo={handleRequestDemo}
        />

        {/* 9. Admin Panel */}
        <ModuleBlock
          id="admin"
          category="Governance & Master Configuration"
          title={AdminContent.title}
          content={AdminContent.text}
          contentList={AdminContent.contentList}
          icon="admin_ss.png"
          direction="left"
          badge="Role-Based Security"
          onRequestDemo={handleRequestDemo}
        />
      </div>

      {/* Why Choose Divine HMS / Features */}
      <Features />

      {/* Contact & Live Demo Request Form */}
      <Contact />
    </main>
  );
};

export default Home;
